import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, Building2, Calendar, FileText, Users, 
  Settings, Plus, TrendingUp, TrendingDown, DollarSign,
  Clock, CheckCircle, AlertTriangle, Target, Award,
  Star, Heart, ThumbsUp, MessageSquare, Phone, Mail,
  Map, Navigation, Compass, Activity, Globe, MapPin,
  Package, Layers, GitBranch, GitCommit, GitPullRequest,
  GitMerge, Workflow, UserCheck, ArrowRight, ArrowLeft,
  Play, Pause, Stop, RotateCcw, List, Grid, PieChart,
  Monitor, Smartphone, Wifi, Lock, Eye, EyeOff, Save,
  Upload, Trash2, Edit, Check, Clock4,
  CalendarDays, Brain, Palette, Key, Database, Zap,
  Percent, Bell, Search, Filter, Download, RefreshCw,
  Sun, Moon, ChevronRight, ChevronLeft, MoreHorizontal
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart as RechartsPieChart, 
  Pie, Cell, ComposedChart, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';

const ProjexDashboard = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');

  // Mock data for charts and metrics
  const projectData = [
    { id: 1, name: 'Riyadh Mall Construction', status: 'In Progress', phase: 'Construction', progress: 75, budget: 2500000, spent: 1875000, team: 45, deadline: '2024-12-15' },
    { id: 2, name: 'Jeddah Office Complex', status: 'Planning', phase: 'Design', progress: 30, budget: 1800000, spent: 540000, team: 28, deadline: '2025-03-20' },
    { id: 3, name: 'Dammam Industrial Park', status: 'Completed', phase: 'Opening', progress: 100, budget: 3200000, spent: 3200000, team: 62, deadline: '2024-08-30' },
    { id: 4, name: 'Abha Resort Development', status: 'At Risk', phase: 'Construction', progress: 60, budget: 4200000, spent: 2520000, team: 38, deadline: '2025-01-15' },
    { id: 5, name: 'Tabuk University Campus', status: 'In Progress', phase: 'Site Selection', progress: 15, budget: 1500000, spent: 225000, team: 22, deadline: '2025-06-30' }
  ];

  const chartData = [
    { month: 'Jan', projects: 12, completion: 85, budget: 2.1, timeline: 78 },
    { month: 'Feb', projects: 15, completion: 88, budget: 2.3, timeline: 82 },
    { month: 'Mar', projects: 18, completion: 92, budget: 2.8, timeline: 85 },
    { month: 'Apr', projects: 22, completion: 89, budget: 3.2, timeline: 79 },
    { month: 'May', projects: 25, completion: 94, budget: 3.5, timeline: 87 },
    { month: 'Jun', projects: 28, completion: 91, budget: 3.8, timeline: 84 }
  ];

  const phaseData = [
    { name: 'Site Selection', value: 15, color: '#8B5CF6' },
    { name: 'Design', value: 25, color: '#3B82F6' },
    { name: 'Construction', value: 45, color: '#F59E0B' },
    { name: 'Opening', value: 15, color: '#10B981' }
  ];

  const performanceData = [
    { subject: 'Timeline', A: 85, fullMark: 100 },
    { subject: 'Budget', A: 78, fullMark: 100 },
    { subject: 'Quality', A: 92, fullMark: 100 },
    { subject: 'Safety', A: 88, fullMark: 100 },
    { subject: 'Team', A: 90, fullMark: 100 }
  ];

  const COLORS = ['#8B5CF6', '#3B82F6', '#F59E0B', '#10B981', '#EF4444'];

  const MetricCard = ({ title, value, change, icon: Icon, color, isDarkMode }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className={`p-6 rounded-2xl backdrop-blur-sm border ${
        isDarkMode 
          ? 'bg-white/10 border-white/20' 
          : 'bg-white/70 border-gray-200/50'
      } shadow-lg hover:shadow-xl transition-all duration-300`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {title}
          </p>
          <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {value}
          </p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${
              change > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {change > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const ProjectCard = ({ project, isDarkMode }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`p-6 rounded-2xl backdrop-blur-sm border ${
        isDarkMode 
          ? 'bg-white/10 border-white/20' 
          : 'bg-white/70 border-gray-200/50'
      } shadow-lg hover:shadow-xl transition-all duration-300`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {project.name}
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {project.phase} Phase
          </p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          project.status === 'Completed' ? 'bg-green-100 text-green-800' :
          project.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
          project.status === 'At Risk' ? 'bg-red-100 text-red-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {project.status}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Progress</span>
            <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{project.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-purple-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${project.progress}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Budget</p>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              ${(project.budget / 1000000).toFixed(1)}M
            </p>
          </div>
          <div>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Team</p>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {project.team} members
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Due {new Date(project.deadline).toLocaleDateString()}
            </span>
          </div>
          <button className={`p-2 rounded-lg transition-all ${
            isDarkMode 
              ? 'hover:bg-white/10 text-gray-300' 
              : 'hover:bg-gray-100 text-gray-600'
          }`}>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );

  const ChartCard = ({ title, children, isDarkMode }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-6 rounded-2xl backdrop-blur-sm border ${
        isDarkMode 
          ? 'bg-white/10 border-white/20' 
          : 'bg-white/70 border-gray-200/50'
      } shadow-lg`}
    >
      <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        {title}
      </h3>
      {children}
    </motion.div>
  );

  return (
    <div className="min-h-screen p-6">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Projex Dashboard
            </h1>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Comprehensive project management and tracking platform
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all ${
                isDarkMode 
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                  : 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span>New Project</span>
            </motion.button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Active Projects"
            value={projectData.length}
            change={12}
            icon={Building2}
            color="bg-gradient-to-r from-purple-500 to-indigo-600"
            isDarkMode={isDarkMode}
          />
          <MetricCard
            title="Total Budget"
            value={`$${(projectData.reduce((sum, p) => sum + p.budget, 0) / 1000000).toFixed(1)}M`}
            change={-5}
            icon={DollarSign}
            color="bg-gradient-to-r from-green-500 to-emerald-600"
            isDarkMode={isDarkMode}
          />
          <MetricCard
            title="Team Members"
            value={projectData.reduce((sum, p) => sum + p.team, 0)}
            change={8}
            icon={Users}
            color="bg-gradient-to-r from-blue-500 to-cyan-600"
            isDarkMode={isDarkMode}
          />
          <MetricCard
            title="Completion Rate"
            value={`${Math.round(projectData.reduce((sum, p) => sum + p.progress, 0) / projectData.length)}%`}
            change={15}
            icon={Target}
            color="bg-gradient-to-r from-orange-500 to-red-600"
            isDarkMode={isDarkMode}
          />
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Charts */}
        <div className="lg:col-span-2 space-y-8">
          {/* Performance Overview Chart */}
          <ChartCard title="Performance Overview" isDarkMode={isDarkMode}>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                <XAxis 
                  dataKey="month" 
                  stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} 
                />
                <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                    border: isDarkMode ? '1px solid #374151' : '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="completion" 
                  stackId="1" 
                  stroke="#8B5CF6" 
                  fill="#8B5CF6" 
                  fillOpacity={0.3} 
                />
                <Bar dataKey="projects" fill="#3B82F6" />
                <Line type="monotone" dataKey="timeline" stroke="#F59E0B" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Project Phase Distribution */}
          <ChartCard title="Project Phase Distribution" isDarkMode={isDarkMode}>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={phaseData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {phaseData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                    border: isDarkMode ? '1px solid #374151' : '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
              </RechartsPieChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Performance Radar Chart */}
          <ChartCard title="Performance Metrics" isDarkMode={isDarkMode}>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={performanceData}>
                <PolarGrid stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                <PolarAngleAxis 
                  dataKey="subject" 
                  tick={{ fill: isDarkMode ? '#9CA3AF' : '#6B7280' }} 
                />
                <PolarRadiusAxis 
                  angle={90} 
                  domain={[0, 100]} 
                  tick={{ fill: isDarkMode ? '#9CA3AF' : '#6B7280' }} 
                />
                <Radar
                  name="Performance"
                  dataKey="A"
                  stroke="#8B5CF6"
                  fill="#8B5CF6"
                  fillOpacity={0.3}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                    border: isDarkMode ? '1px solid #374151' : '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Right Column - Projects List */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Recent Projects
            </h2>
            <button className={`p-2 rounded-lg transition-all ${
              isDarkMode 
                ? 'hover:bg-white/10 text-gray-300' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}>
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            {projectData.map((project, index) => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                isDarkMode={isDarkMode}
              />
            ))}
          </div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-6 rounded-2xl backdrop-blur-sm border ${
              isDarkMode 
                ? 'bg-white/10 border-white/20' 
                : 'bg-white/70 border-gray-200/50'
            } shadow-lg`}
          >
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Quick Actions
            </h3>
            <div className="space-y-3">
              {[
                { icon: Plus, label: 'Create New Project', color: 'from-purple-500 to-indigo-600' },
                { icon: Users, label: 'Manage Team', color: 'from-blue-500 to-cyan-600' },
                { icon: FileText, label: 'Upload Documents', color: 'from-green-500 to-emerald-600' },
                { icon: BarChart3, label: 'Generate Report', color: 'from-orange-500 to-red-600' }
              ].map((action, index) => (
                <motion.button
                  key={index}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full flex items-center space-x-3 p-3 rounded-xl transition-all ${
                    isDarkMode 
                      ? 'hover:bg-white/10 text-gray-300' 
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <div className={`p-2 rounded-lg bg-gradient-to-r ${action.color}`}>
                    <action.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-medium">{action.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProjexDashboard; 